/**
 * Config for the API endpoint test harness (/endpoint-test).
 *
 * Each entry is one *ready-on-the-backend-but-unwired-in-this-frontend* endpoint
 * surfaced by the "what's built but not implemented" audit. The screen renders a
 * card per entry: editable params, an optional JSON body, a Run button, and the
 * live response. Calls go through the same `losHttp` client the rest of the app
 * uses, so auth + logging behave identically.
 *
 * `run(http, ctx)` returns the raw axios promise. `ctx` = {
 *   loanId,            // the global Loan ID control (deal `_id`)
 *   connectionId,      // the global Web PubSub connection id control (chat)
 *   f,                 // { [fieldKey]: stringValue } from this card's inputs
 *   body,              // parsed JSON from this card's body editor (or undefined)
 * }
 *
 * Defaults target the loan the user pinned for testing:
 *   6a20ae2f82a9a6bb5f36e597
 */

export const DEFAULT_LOAN_ID = "6a20ae2f82a9a6bb5f36e597";

/**
 * Endpoints WIRED INTO real product code (not just callable here). The screen
 * splits the list into "Pending" vs "Completed" off this set.
 *
 * Verified 2026-08-20 by matching every path below against a real call site
 * outside this screen — a hook/api method that a component actually invokes, not
 * merely a definition or a comment. Surfaces:
 *   • Per-loan chat (Chat tab + floating bubble)
 *   • Document extracts viewer (Documents tab)
 *   • Counterparty card / find-contact lookup (Loan Info + contact sidebar)
 *   • Comment edit (currently disabled, but it was integrated)
 *   • Document share packages (useLoanFilesHook — download + push)
 *   • Ocrolus book lifecycle (Documents tab → Ocrolus)
 *   • Missing documents (Documents tab → Missing)
 *   • Admin → Market Data (ZHVI), Guidelines, Pricing (RTL + DSCR)
 *   • Correspondents → Drilldown (contacts, agreements, overlays, editor)
 */
export const COMPLETED_ENDPOINT_IDS = new Set([
  // Loan drilldown
  "chat-history",
  "chat-send",
  "chat-join",
  "chat-leave",
  "extracts-index",
  "extract-bytes",
  "extract-edit",
  "add-counterparty",
  "update-comment",
  "find-contact",
  "document-package-download",
  "push-document-package",
  "ocrolus-create-book",
  "ocrolus-book-status",
  "ocrolus-books-list",
  "ocrolus-assign-book",
  // Documents tab → Missing (the GET variant only; the POST twin is unused)
  "missing-docs-get",

  // Admin → Market Data (AdminMarketDataScreen + ZhviZipPanel)
  "zhvi-versions",
  "zhvi-zip",
  "zhvi-acknowledge",
  "zhvi-rollback",

  // Admin → Guidelines (AdminGuidelinesScreen + GuidelineRulesPanel)
  "guidelines-list",
  "guideline-get",
  "guideline-log",
  "guideline-put-md",
  "guideline-put-structured",
  "guideline-codify",
  "guideline-revert",

  // Admin → Pricing → RTL (RtlPricingPane + RtlRatePreview)
  "rtl-pricing-get",
  "rtl-pricing-log",
  "rtl-pricing-put",
  "rtl-pricing-preview",
  "rtl-pricing-seed",

  // Admin → Pricing → DSCR (DscrPricingPane + DscrDiffDialog + DscrAgentReviewDialog)
  "dscr-pricing-list",
  "dscr-pricing-get",
  "dscr-pricing-log",
  "dscr-pricing-put",
  "dscr-pricing-diff",
  "dscr-pricing-revert",
  "dscr-pricing-agent-review",

  // Correspondents → Drilldown (contacts / agreements / overlays / editor tabs)
  "corr-contact-add",
  "corr-contact-update",
  "corr-contact-delete",
  "corr-document-download",
  "corr-document-status",
  "corr-mlpa-versions",
  "corr-mlpa-diff",
  "corr-overlays-list",
  "corr-editor-get",
  "corr-editor-save",
]);

/**
 * Endpoints that have a method sitting in a hook/api module but NO caller — one
 * line of UI away from done. Kept in Pending (they have no product surface) but
 * called out so nobody re-implements the client half.
 *
 *   corr-documents-list     useCorrespondentsHook.listDocuments      (the
 *                           Agreements tab reads agreementVersions instead)
 *   corr-index-terms        useCorrespondentsHook.indexTerms
 *   corr-attribute-by-email useCorrespondentsHook.attributeByEmail
 */
export const CLIENT_READY_ENDPOINT_IDS = new Set([
  "corr-documents-list",
  "corr-index-terms",
  "corr-attribute-by-email",
]);

/**
 * Groups whose router is mounted ONLY by the listener service
 * (`app/listener/web.py`), not by the main app. `losHttp` has a single LOS base
 * URL, so every endpoint in these groups 404s from here regardless of auth.
 *
 * Kept in the config as a reference for what exists, flagged so nobody reads a
 * 404 as a bug in their request. Reaching them needs a base-URL decision first
 * (proxy through the main app, or a second axios client).
 */
export const LISTENER_HOSTED_GROUPS = new Set([
  "AI reviews — appraisal, sizer & eval (ai_reviews.py)",
  "Admin — loan-model schemas",
  "Admin — agent skills",
]);

// Small helpers so query strings stay clean (drop empty optionals).
const params = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== "" && v != null) out[k] = v;
  });
  return out;
};

export const ENDPOINT_GROUPS = [
  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Checklists — missing documents",
    blurb:
      "Stateless, quantity-aware check of what docs a loan still needs (per guarantor / entity / property). Read-only — never persists. Documented in missing-documents-check.md.",
    endpoints: [
      {
        id: "missing-docs-get",
        method: "GET",
        label: "Check missing documents",
        path: "/checklists/check_missing_documents/{dealId}",
        needsLoan: true,
        run: (http, c) =>
          http.get(`/checklists/check_missing_documents/${c.loanId}`),
      },
      {
        id: "missing-docs-post",
        method: "POST",
        label: "Check missing documents (POST variant)",
        path: "/checklists/check_missing_documents/{dealId}",
        needsLoan: true,
        run: (http, c) =>
          http.post(`/checklists/check_missing_documents/${c.loanId}`, {}),
      },
      {
        id: "remove-assoc",
        method: "POST",
        label: "Remove association from underwriting requirement",
        path: "/checklists/remove_association_from_underwriting_requirement/{dealId}/{requirementId}",
        needsLoan: true,
        fields: [
          { key: "requirementId", label: "requirementId", placeholder: "condition _id" },
        ],
        body: JSON.stringify({ borrowers: ["<borrowerId>"] }, null, 2),
        bodyHelp:
          "Map of associationType → [ids to pull]. Keys: borrowers / subjectProperties / entities / loanDocuments.",
        run: (http, c) =>
          http.post(
            `/checklists/remove_association_from_underwriting_requirement/${c.loanId}/${c.f.requirementId}`,
            c.body,
          ),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Per-loan live chat",
    blurb:
      "Real-time team chat per loan over the shared Web PubSub socket. History is durable; join/leave manage the live room (need a connectionId from /realtime/negotiate). Documented in loan-chat.md.",
    endpoints: [
      {
        id: "chat-history",
        method: "GET",
        label: "Get chat history (oldest-first)",
        path: "/chat/{dealId}/messages",
        needsLoan: true,
        fields: [
          { key: "channel", label: "channel", default: "general", width: 140 },
          { key: "limit", label: "limit", default: "50", width: 100 },
          { key: "before_id", label: "before_id (paging)", placeholder: "oldest _id you hold" },
        ],
        run: (http, c) =>
          http.get(`/chat/${c.loanId}/messages`, {
            params: params({
              channel: c.f.channel,
              limit: c.f.limit,
              before_id: c.f.before_id,
            }),
          }),
      },
      {
        id: "chat-send",
        method: "POST",
        label: "Send chat message",
        path: "/chat/{dealId}/messages",
        needsLoan: true,
        body: JSON.stringify(
          { htmlString: "<p>Test message from the endpoint tester</p>" },
          null,
          2,
        ),
        bodyHelp: "{ htmlString | text, channel?, mentions? }",
        run: (http, c) => http.post(`/chat/${c.loanId}/messages`, c.body),
      },
      {
        id: "chat-join",
        method: "POST",
        label: "Join chat room (start receiving live)",
        path: "/chat/{dealId}/join",
        needsLoan: true,
        usesConnection: true,
        fields: [{ key: "channel", label: "channel", default: "general", width: 140 }],
        bodyHelp:
          "Sends { connectionId, channel }. connectionId comes from the global control above. 503 = realtime/Web PubSub not provisioned.",
        run: (http, c) =>
          http.post(`/chat/${c.loanId}/join`, {
            connectionId: c.connectionId,
            channel: c.f.channel,
          }),
      },
      {
        id: "chat-leave",
        method: "POST",
        label: "Leave chat room",
        path: "/chat/{dealId}/leave",
        needsLoan: true,
        usesConnection: true,
        fields: [{ key: "channel", label: "channel", default: "general", width: 140 }],
        bodyHelp: "Sends { connectionId, channel }.",
        run: (http, c) =>
          http.post(`/chat/${c.loanId}/leave`, {
            connectionId: c.connectionId,
            channel: c.f.channel,
          }),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Document extracts viewer (/files)",
    blurb:
      "The 3-pane OCR viewer model: index of extracted artifacts grouped under each parent doc, byte-fetch per artifact, and an editable copy (AI original is never overwritten). Documented in document-extracts-viewer.md.",
    endpoints: [
      {
        id: "extracts-index",
        method: "GET",
        label: "Extracts index (viewer model)",
        path: "/files/extracts/{dealId}",
        needsLoan: true,
        run: (http, c) => http.get(`/files/extracts/${c.loanId}`),
      },
      {
        id: "extract-bytes",
        method: "GET",
        label: "Extract bytes (md / json / image)",
        path: "/files/extract_bytes/{dealId}/{blob_name}",
        needsLoan: true,
        fields: [
          {
            key: "blob_name",
            label: "blob_name",
            placeholder: "e.g. <oid>.text.md  (from the index above)",
            width: 380,
          },
          { key: "original", label: "original? (true/false)", default: "false", width: 160 },
        ],
        responseType: "blob",
        run: (http, c) =>
          http.get(
            `/files/extract_bytes/${c.loanId}/${encodeURIComponent(c.f.blob_name)}`,
            {
              params: params({ original: c.f.original === "true" ? "true" : "" }),
              responseType: "blob",
            },
          ),
      },
      {
        id: "extract-edit",
        method: "PUT",
        label: "Save extract edit (content / notes)",
        path: "/files/extract_edit/{dealId}/{blob_name}",
        needsLoan: true,
        fields: [
          { key: "blob_name", label: "blob_name", placeholder: "<oid>.text.md", width: 380 },
        ],
        body: JSON.stringify(
          { content: "# Edited via tester\n\nhello", notes: "test note" },
          null,
          2,
        ),
        bodyHelp:
          "{ content?, notes? } — content is a string (markdown) or object (structured_json). Both optional.",
        run: (http, c) =>
          http.put(
            `/files/extract_edit/${c.loanId}/${encodeURIComponent(c.f.blob_name)}`,
            c.body,
          ),
      },
      {
        id: "wire-worksheet",
        method: "GET",
        label: "Wire worksheet (.xlsx download)",
        path: "/files/wire_worksheet/{dealId}",
        needsLoan: true,
        responseType: "blob",
        run: (http, c) =>
          http.get(`/files/wire_worksheet/${c.loanId}`, { responseType: "blob" }),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Underwriting — counterparty, comments, contacts",
    blurb:
      "Loan-level write actions the FE doesn't expose yet. Each mutating call returns the updated full loan object.",
    endpoints: [
      {
        id: "add-counterparty",
        method: "POST",
        label: "Add loan counterparty (investor)",
        path: "/underwriting/add_loan_counterparty/{dealId}",
        needsLoan: true,
        body: JSON.stringify({ investorCounterparty: "Test Investor" }, null, 2),
        bodyHelp: "{ investorCounterparty }",
        run: (http, c) =>
          http.post(`/underwriting/add_loan_counterparty/${c.loanId}`, c.body),
      },
      {
        id: "update-comment",
        method: "POST",
        label: "Update (edit) an existing comment",
        path: "/underwriting/update_comment/{dealId}/{commentId}",
        needsLoan: true,
        fields: [{ key: "commentId", label: "commentId", placeholder: "comment _id" }],
        body: JSON.stringify({ htmlString: "<p>edited comment</p>" }, null, 2),
        bodyHelp: "Partial comment fields to $set (e.g. { htmlString }).",
        run: (http, c) =>
          http.post(
            `/underwriting/update_comment/${c.loanId}/${c.f.commentId}`,
            c.body,
          ),
      },
      {
        id: "find-contact",
        method: "POST",
        label: "Find contact by email",
        path: "/underwriting/find_contact_by_email",
        needsLoan: false,
        body: JSON.stringify({ emailAddress: "test@example.com" }, null, 2),
        bodyHelp: "{ emailAddress, phone?, firstName?, company?, loanRole? }",
        run: (http, c) =>
          http.post(`/underwriting/find_contact_by_email`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Underwriting — borrower-portal data sync (loan ↔ portal)",
    blurb:
      "The push/pull half of borrower-portal linking (user-linking is already wired; this moves files/experience/entities). Each needs a borrowerId from the loan's `borrowers[]`. The borrower must already be linked to a portal user.",
    endpoints: [
      {
        id: "push-files",
        method: "POST",
        label: "Push files → borrower portal",
        path: "/underwriting/push_files_to_borrower/{dealId}/{borrowerId}",
        needsLoan: true,
        fields: [{ key: "borrowerId", label: "borrowerId" }],
        body: JSON.stringify({ fileIds: [] }, null, 2),
        bodyHelp: "{ fileIds: [file_storage_name, ...] }",
        run: (http, c) =>
          http.post(
            `/underwriting/push_files_to_borrower/${c.loanId}/${c.f.borrowerId}`,
            c.body,
          ),
      },
      {
        id: "pull-files",
        method: "POST",
        label: "Pull files ← borrower portal",
        path: "/underwriting/pull_files_to_loan/{dealId}/{borrowerId}",
        needsLoan: true,
        fields: [{ key: "borrowerId", label: "borrowerId" }],
        body: JSON.stringify({ fileIds: [] }, null, 2),
        bodyHelp: "{ fileIds: [file_storage_name, ...] }",
        run: (http, c) =>
          http.post(
            `/underwriting/pull_files_to_loan/${c.loanId}/${c.f.borrowerId}`,
            c.body,
          ),
      },
      {
        id: "push-experience",
        method: "POST",
        label: "Push experience → borrower portal",
        path: "/underwriting/push_experience_to_borrower/{dealId}/{borrowerId}",
        needsLoan: true,
        fields: [{ key: "borrowerId", label: "borrowerId" }],
        run: (http, c) =>
          http.post(
            `/underwriting/push_experience_to_borrower/${c.loanId}/${c.f.borrowerId}`,
            {},
          ),
      },
      {
        id: "pull-experience",
        method: "POST",
        label: "Pull experience ← borrower portal",
        path: "/underwriting/pull_experience_to_loan/{dealId}/{borrowerId}",
        needsLoan: true,
        fields: [{ key: "borrowerId", label: "borrowerId" }],
        run: (http, c) =>
          http.post(
            `/underwriting/pull_experience_to_loan/${c.loanId}/${c.f.borrowerId}`,
            {},
          ),
      },
      {
        id: "push-entities",
        method: "POST",
        label: "Push entities → borrower portal",
        path: "/underwriting/push_entities_to_borrower/{dealId}/{borrowerId}",
        needsLoan: true,
        fields: [{ key: "borrowerId", label: "borrowerId" }],
        run: (http, c) =>
          http.post(
            `/underwriting/push_entities_to_borrower/${c.loanId}/${c.f.borrowerId}`,
            {},
          ),
      },
      {
        id: "pull-entities",
        method: "POST",
        label: "Pull entities ← borrower portal",
        path: "/underwriting/pull_entities_to_loan/{dealId}/{borrowerId}",
        needsLoan: true,
        fields: [{ key: "borrowerId", label: "borrowerId" }],
        run: (http, c) =>
          http.post(
            `/underwriting/pull_entities_to_loan/${c.loanId}/${c.f.borrowerId}`,
            {},
          ),
      },
      {
        id: "create-portal-invite",
        method: "POST",
        label: "Create portal borrower + send invite",
        path: "/underwriting/create_portal_borrower_with_invite/{dealId}/{borrowerId}",
        needsLoan: true,
        fields: [{ key: "borrowerId", label: "borrowerId" }],
        run: (http, c) =>
          http.post(
            `/underwriting/create_portal_borrower_with_invite/${c.loanId}/${c.f.borrowerId}`,
            {},
          ),
      },
      {
        id: "create-portal-no-invite",
        method: "POST",
        label: "Create portal borrower (no invite)",
        path: "/underwriting/create_portal_borrower_no_invite/{dealId}/{borrowerId}",
        needsLoan: true,
        fields: [{ key: "borrowerId", label: "borrowerId" }],
        run: (http, c) =>
          http.post(
            `/underwriting/create_portal_borrower_no_invite/${c.loanId}/${c.f.borrowerId}`,
            {},
          ),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Users / admin",
    blurb:
      "Admin user actions not wired into the Users screen. grant_admin / update_user_by_email are admin-gated server-side; invalidate_session_cache is intentionally auth-free.",
    endpoints: [
      {
        id: "grant-admin",
        method: "POST",
        label: "Grant / revoke admin tag",
        path: "/users/grant_admin/{email}",
        needsLoan: false,
        fields: [{ key: "email", label: "email", placeholder: "user@company.com", width: 280 }],
        body: JSON.stringify({ is_admin: true }, null, 2),
        bodyHelp: "{ is_admin: true|false } (defaults true). Admin-only.",
        run: (http, c) =>
          http.post(`/users/grant_admin/${encodeURIComponent(c.f.email)}`, c.body),
      },
      {
        id: "update-user-email",
        method: "POST",
        label: "Update user by email",
        path: "/users/update_user_by_email/{email_address}",
        needsLoan: false,
        fields: [{ key: "email", label: "email", placeholder: "user@company.com", width: 280 }],
        body: JSON.stringify({ phoneNumber: "555-0100" }, null, 2),
        bodyHelp: "Partial $set of user fields.",
        run: (http, c) =>
          http.post(
            `/users/update_user_by_email/${encodeURIComponent(c.f.email)}`,
            c.body,
          ),
      },
      {
        id: "invalidate-session",
        method: "POST",
        label: "Invalidate session cache (this token)",
        path: "/users/invalidate_session_cache",
        needsLoan: false,
        run: (http) => http.post(`/users/invalidate_session_cache`, {}),
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // Below: the "available on the backend but not wired in the FE" endpoints from
  // the endpoint-gap audit. MAIN-APP routers only (reachable through losHttp).
  // The listener /admin/* "explorer" workbench (~120 endpoints) runs on a
  // separate service the FE base URL can't reach, so it's documented in
  // INTEGRATION_BACKLOG.md rather than added here. Destructive backfill/rebuild
  // ops are intentionally omitted everywhere.
  // ══════════════════════════════════════════════════════════════════════════

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Admin Reference Data — market data (ZHVI)",
    blurb:
      "Versioned Zillow ZHVI ingest + per-zip lookup (admin only). Upload is multipart (a CSV) so it isn't testable here — use versions / zip / acknowledge / rollback. /admin/data/zhvi/*.",
    endpoints: [
      {
        id: "zhvi-versions",
        method: "GET",
        label: "List ZHVI versions",
        path: "/admin/data/zhvi/versions",
        needsLoan: false,
        run: (http) => http.get(`/admin/data/zhvi/versions`),
      },
      {
        id: "zhvi-zip",
        method: "GET",
        label: "ZHVI for a ZIP",
        path: "/admin/data/zhvi/zip/{zip_code}",
        needsLoan: false,
        fields: [{ key: "zip", label: "zip_code", default: "33607", width: 120 }],
        run: (http, c) => http.get(`/admin/data/zhvi/zip/${c.f.zip}`),
      },
      {
        id: "zhvi-acknowledge",
        method: "POST",
        label: "Acknowledge a ZHVI version",
        path: "/admin/data/zhvi/versions/{version_id}/acknowledge",
        needsLoan: false,
        fields: [{ key: "versionId", label: "version_id", placeholder: "version _id", width: 260 }],
        run: (http, c) =>
          http.post(`/admin/data/zhvi/versions/${c.f.versionId}/acknowledge`, {}),
      },
      {
        id: "zhvi-rollback",
        method: "POST",
        label: "Roll back to previous ZHVI version",
        path: "/admin/data/zhvi/rollback",
        needsLoan: false,
        body: JSON.stringify({}, null, 2),
        bodyHelp: "Optional { versionId } to target a specific version; omit to roll back the latest.",
        run: (http, c) => http.post(`/admin/data/zhvi/rollback`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Admin Reference Data — underwriting guidelines",
    blurb:
      "Versioned guideline docs (markdown + structured), a codify step, and per-slug revert/log (admin only). PDF import is multipart so it isn't testable here. /admin/data/guidelines/*.",
    endpoints: [
      {
        id: "guidelines-list",
        method: "GET",
        label: "List guidelines",
        path: "/admin/data/guidelines",
        needsLoan: false,
        run: (http) => http.get(`/admin/data/guidelines`),
      },
      {
        id: "guideline-get",
        method: "GET",
        label: "Get one guideline",
        path: "/admin/data/guidelines/{slug}",
        needsLoan: false,
        fields: [{ key: "slug", label: "slug", placeholder: "e.g. dscr", width: 220 }],
        run: (http, c) => http.get(`/admin/data/guidelines/${c.f.slug}`),
      },
      {
        id: "guideline-log",
        method: "GET",
        label: "Guideline edit log",
        path: "/admin/data/guidelines/{slug}/log",
        needsLoan: false,
        fields: [{ key: "slug", label: "slug", placeholder: "e.g. dscr", width: 220 }],
        run: (http, c) => http.get(`/admin/data/guidelines/${c.f.slug}/log`),
      },
      {
        id: "guideline-put-md",
        method: "PUT",
        label: "Update guideline markdown",
        path: "/admin/data/guidelines/{slug}/markdown",
        needsLoan: false,
        fields: [{ key: "slug", label: "slug", width: 220 }],
        body: JSON.stringify({ markdown: "# Edited via tester" }, null, 2),
        bodyHelp: "{ markdown }",
        run: (http, c) =>
          http.put(`/admin/data/guidelines/${c.f.slug}/markdown`, c.body),
      },
      {
        id: "guideline-put-structured",
        method: "PUT",
        label: "Update guideline structured JSON",
        path: "/admin/data/guidelines/{slug}/structured",
        needsLoan: false,
        fields: [{ key: "slug", label: "slug", width: 220 }],
        body: JSON.stringify({ structured: {} }, null, 2),
        bodyHelp: "{ structured: {...} }",
        run: (http, c) =>
          http.put(`/admin/data/guidelines/${c.f.slug}/structured`, c.body),
      },
      {
        id: "guideline-codify",
        method: "POST",
        label: "Codify guideline (→ structured rules)",
        path: "/admin/data/guidelines/{slug}/codify",
        needsLoan: false,
        fields: [{ key: "slug", label: "slug", width: 220 }],
        run: (http, c) => http.post(`/admin/data/guidelines/${c.f.slug}/codify`, {}),
      },
      {
        id: "guideline-revert",
        method: "POST",
        label: "Revert guideline to a version",
        path: "/admin/data/guidelines/{slug}/revert",
        needsLoan: false,
        fields: [{ key: "slug", label: "slug", width: 220 }],
        body: JSON.stringify({ version: 1 }, null, 2),
        bodyHelp: "{ version } to restore.",
        run: (http, c) =>
          http.post(`/admin/data/guidelines/${c.f.slug}/revert`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Admin Reference Data — pricing (RTL + DSCR)",
    blurb:
      "RTL pricing config (sections + preview + seed) and per-correspondent DSCR pricing sheets (get/put/diff/revert/log), admin only. DSCR import is multipart so it isn't testable here. /admin/data/rtl-pricing/* + /admin/data/dscr-pricing/*.",
    endpoints: [
      {
        id: "rtl-pricing-get",
        method: "GET",
        label: "Get RTL pricing config",
        path: "/admin/data/rtl-pricing",
        needsLoan: false,
        run: (http) => http.get(`/admin/data/rtl-pricing`),
      },
      {
        id: "rtl-pricing-log",
        method: "GET",
        label: "RTL pricing edit log",
        path: "/admin/data/rtl-pricing/log",
        needsLoan: false,
        run: (http) => http.get(`/admin/data/rtl-pricing/log`),
      },
      {
        id: "rtl-pricing-put",
        method: "PUT",
        label: "Update an RTL pricing section",
        path: "/admin/data/rtl-pricing/{section}",
        needsLoan: false,
        fields: [{ key: "section", label: "section", placeholder: "e.g. base_rates", width: 220 }],
        body: JSON.stringify({}, null, 2),
        bodyHelp: "The section payload to $set.",
        run: (http, c) =>
          http.put(`/admin/data/rtl-pricing/${c.f.section}`, c.body),
      },
      {
        id: "rtl-pricing-preview",
        method: "POST",
        label: "Preview RTL pricing impact",
        path: "/admin/data/rtl-pricing/preview",
        needsLoan: false,
        body: JSON.stringify({}, null, 2),
        bodyHelp: "Proposed changes to preview (no write).",
        run: (http, c) => http.post(`/admin/data/rtl-pricing/preview`, c.body),
      },
      {
        id: "rtl-pricing-seed",
        method: "POST",
        label: "Seed RTL pricing defaults",
        path: "/admin/data/rtl-pricing/seed",
        needsLoan: false,
        run: (http) => http.post(`/admin/data/rtl-pricing/seed`, {}),
      },
      {
        id: "dscr-pricing-list",
        method: "GET",
        label: "List DSCR pricing (all correspondents)",
        path: "/admin/data/dscr-pricing",
        needsLoan: false,
        run: (http) => http.get(`/admin/data/dscr-pricing`),
      },
      {
        id: "dscr-pricing-get",
        method: "GET",
        label: "Get DSCR pricing for a correspondent",
        path: "/admin/data/dscr-pricing/{cp_id}",
        needsLoan: false,
        fields: [{ key: "cpId", label: "cp_id", placeholder: "correspondent id", width: 220 }],
        run: (http, c) => http.get(`/admin/data/dscr-pricing/${c.f.cpId}`),
      },
      {
        id: "dscr-pricing-log",
        method: "GET",
        label: "DSCR pricing edit log",
        path: "/admin/data/dscr-pricing/{cp_id}/log",
        needsLoan: false,
        fields: [{ key: "cpId", label: "cp_id", width: 220 }],
        run: (http, c) => http.get(`/admin/data/dscr-pricing/${c.f.cpId}/log`),
      },
      {
        id: "dscr-pricing-put",
        method: "PUT",
        label: "Update DSCR pricing for a correspondent",
        path: "/admin/data/dscr-pricing/{cp_id}",
        needsLoan: false,
        fields: [{ key: "cpId", label: "cp_id", width: 220 }],
        body: JSON.stringify({}, null, 2),
        bodyHelp: "The DSCR pricing payload to $set.",
        run: (http, c) => http.put(`/admin/data/dscr-pricing/${c.f.cpId}`, c.body),
      },
      {
        id: "dscr-pricing-diff",
        method: "POST",
        label: "Diff a proposed DSCR sheet",
        path: "/admin/data/dscr-pricing/{cp_id}/diff",
        needsLoan: false,
        fields: [{ key: "cpId", label: "cp_id", width: 220 }],
        body: JSON.stringify({}, null, 2),
        bodyHelp: "Proposed sheet to diff against the stored one.",
        run: (http, c) =>
          http.post(`/admin/data/dscr-pricing/${c.f.cpId}/diff`, c.body),
      },
      {
        id: "dscr-pricing-revert",
        method: "POST",
        label: "Revert DSCR pricing to a version",
        path: "/admin/data/dscr-pricing/{cp_id}/revert",
        needsLoan: false,
        fields: [{ key: "cpId", label: "cp_id", width: 220 }],
        body: JSON.stringify({ version: 1 }, null, 2),
        bodyHelp: "{ version } to restore.",
        run: (http, c) =>
          http.post(`/admin/data/dscr-pricing/${c.f.cpId}/revert`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "AI reviews — appraisal, sizer & eval (ai_reviews.py)",
    blurb:
      "⚠️ LISTENER-HOSTED — these 404 from this app. The router is mounted only by app/listener/web.py, not main.py. The SECOND ai-reviews router the FE never calls (the drilldown uses agent_reviews.py). Appraisal + sizer AI reviews, a per-loan overview, per-doc review, and eval tooling. Admin only. /admin/loans/{dealId}/ai-reviews/* + /admin/loans/ai-conditions/*.",
    endpoints: [
      {
        id: "aireview-overview",
        method: "GET",
        label: "AI reviews overview (per loan)",
        path: "/admin/loans/{dealId}/ai-reviews/overview",
        needsLoan: true,
        run: (http, c) => http.get(`/admin/loans/${c.loanId}/ai-reviews/overview`),
      },
      {
        id: "aireview-appraisals",
        method: "GET",
        label: "Appraisal review results",
        path: "/admin/loans/{dealId}/ai-reviews/appraisals",
        needsLoan: true,
        run: (http, c) => http.get(`/admin/loans/${c.loanId}/ai-reviews/appraisals`),
      },
      {
        id: "aireview-appraisals-run",
        method: "POST",
        label: "Run appraisal review",
        path: "/admin/loans/{dealId}/ai-reviews/appraisals/run",
        needsLoan: true,
        run: (http, c) =>
          http.post(`/admin/loans/${c.loanId}/ai-reviews/appraisals/run`, {}),
      },
      {
        id: "aireview-sizer",
        method: "GET",
        label: "Sizer review results",
        path: "/admin/loans/{dealId}/ai-reviews/sizer",
        needsLoan: true,
        run: (http, c) => http.get(`/admin/loans/${c.loanId}/ai-reviews/sizer`),
      },
      {
        id: "aireview-sizer-run",
        method: "POST",
        label: "Run sizer review",
        path: "/admin/loans/{dealId}/ai-reviews/sizer/run",
        needsLoan: true,
        run: (http, c) =>
          http.post(`/admin/loans/${c.loanId}/ai-reviews/sizer/run`, {}),
      },
      {
        id: "aireview-doc",
        method: "GET",
        label: "One doc's review payload",
        path: "/admin/loans/{dealId}/ai-reviews/doc/{review_key}",
        needsLoan: true,
        fields: [{ key: "reviewKey", label: "review_key", placeholder: "e.g. appraisal", width: 220 }],
        run: (http, c) =>
          http.get(`/admin/loans/${c.loanId}/ai-reviews/doc/${c.f.reviewKey}`),
      },
      {
        id: "aiconditions-eval-rollup",
        method: "GET",
        label: "AI-conditions eval rollup (global)",
        path: "/admin/loans/ai-conditions/eval-rollup",
        needsLoan: false,
        run: (http) => http.get(`/admin/loans/ai-conditions/eval-rollup`),
      },
      {
        id: "aiconditions-few-shot",
        method: "GET",
        label: "AI-conditions few-shot examples (global)",
        path: "/admin/loans/ai-conditions/few-shot-examples",
        needsLoan: false,
        run: (http) => http.get(`/admin/loans/ai-conditions/few-shot-examples`),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Admin — loan-model schemas",
    blurb:
      "⚠️ LISTENER-HOSTED — these 404 from this app. admin_schemas.py is mounted only by app/listener/web.py. NOTE AdminSchemasScreen calls these over losHttp too, so that screen is broken for the same reason. View/edit the stored loan-model schemas + their AG Grid column-defs and field mappings (admin only). Reads are safe; POST/PUT/DELETE (create/update/delete a schema) are omitted here. /admin/schemas/*.",
    endpoints: [
      {
        id: "schemas-list",
        method: "GET",
        label: "List schemas",
        path: "/admin/schemas",
        needsLoan: false,
        run: (http) => http.get(`/admin/schemas`),
      },
      {
        id: "schema-get",
        method: "GET",
        label: "Get one schema",
        path: "/admin/schemas/{slug}",
        needsLoan: false,
        fields: [{ key: "slug", label: "slug", placeholder: "schema slug", width: 220 }],
        run: (http, c) => http.get(`/admin/schemas/${c.f.slug}`),
      },
      {
        id: "schema-field-mapping",
        method: "GET",
        label: "Schema field mapping",
        path: "/admin/schemas/{slug}/field-mapping",
        needsLoan: false,
        fields: [{ key: "slug", label: "slug", width: 220 }],
        run: (http, c) => http.get(`/admin/schemas/${c.f.slug}/field-mapping`),
      },
      {
        id: "schema-preview",
        method: "GET",
        label: "Schema preview",
        path: "/admin/schemas/{slug}/preview",
        needsLoan: false,
        fields: [{ key: "slug", label: "slug", width: 220 }],
        run: (http, c) => http.get(`/admin/schemas/${c.f.slug}/preview`),
      },
      {
        id: "schema-column-defs",
        method: "GET",
        label: "AG Grid column-defs for a model",
        path: "/admin/schemas/column-defs/{model_name}",
        needsLoan: false,
        fields: [
          { key: "modelName", label: "model_name", placeholder: "e.g. UnderwritingPipelineModel", width: 280 },
        ],
        run: (http, c) => http.get(`/admin/schemas/column-defs/${c.f.modelName}`),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Admin — agent skills",
    blurb:
      "⚠️ LISTENER-HOSTED — these 404 from this app. skills.py is mounted only by app/listener/web.py. Browse the agent skill library — markdown bodies, versions, and reference files (admin only). Reads only here; the PUT/POST/DELETE edit ops are omitted. /admin/skills/*.",
    endpoints: [
      {
        id: "skills-list",
        method: "GET",
        label: "List skills",
        path: "/admin/skills",
        needsLoan: false,
        run: (http) => http.get(`/admin/skills`),
      },
      {
        id: "skill-get",
        method: "GET",
        label: "Get one skill",
        path: "/admin/skills/{group}/{name}",
        needsLoan: false,
        fields: [
          { key: "group", label: "group", placeholder: "skill group", width: 180 },
          { key: "name", label: "name", placeholder: "skill name", width: 180 },
        ],
        run: (http, c) => http.get(`/admin/skills/${c.f.group}/${c.f.name}`),
      },
      {
        id: "skill-versions",
        method: "GET",
        label: "Skill version history",
        path: "/admin/skills/{group}/{name}/versions",
        needsLoan: false,
        fields: [
          { key: "group", label: "group", width: 180 },
          { key: "name", label: "name", width: 180 },
        ],
        run: (http, c) =>
          http.get(`/admin/skills/${c.f.group}/${c.f.name}/versions`),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Correspondent portal (read views)",
    blurb:
      "The correspondent-facing projections — a scoped pipeline + a single-loan view. Not surfaced anywhere in this FE. /correspondent_portal/*.",
    endpoints: [
      {
        id: "corr-portal-pipeline",
        method: "GET",
        label: "Correspondent pipeline",
        path: "/correspondent_portal/pipeline",
        needsLoan: false,
        run: (http) => http.get(`/correspondent_portal/pipeline`),
      },
      {
        id: "corr-portal-loan",
        method: "GET",
        label: "Correspondent loan view",
        path: "/correspondent_portal/loan/{dealId}",
        needsLoan: true,
        run: (http, c) => http.get(`/correspondent_portal/loan/${c.loanId}`),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "FCI servicing performance",
    blurb:
      "Post-close servicing data pulled from FCI — per-loan borrower performance + portfolio status. Read-only. /fci/*.",
    endpoints: [
      {
        id: "fci-borrower-performance",
        method: "GET",
        label: "Borrower performance",
        path: "/fci/borrower_performance/{dealId}",
        needsLoan: true,
        run: (http, c) => http.get(`/fci/borrower_performance/${c.loanId}`),
      },
      {
        id: "fci-portfolio-status",
        method: "GET",
        label: "Portfolio status",
        path: "/fci/portfolio_status/{dealId}",
        needsLoan: true,
        run: (http, c) => http.get(`/fci/portfolio_status/${c.loanId}`),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Document share packages (/files)",
    blurb:
      "Build/download or push a flag-filtered share package (third-party review / counterparty / TPR-approved). Replaces the old trim-by-docType export. /files/document_package + /files/push_document_package.",
    endpoints: [
      {
        id: "document-package-download",
        method: "GET",
        label: "Download share package (.zip)",
        path: "/files/document_package/{dealId}/{selector}",
        needsLoan: true,
        fields: [
          {
            key: "selector",
            label: "selector",
            default: "third_party_review",
            placeholder: "third_party_review | counterparty | tpr_approved",
            width: 280,
          },
        ],
        responseType: "blob",
        run: (http, c) =>
          http.get(`/files/document_package/${c.loanId}/${c.f.selector}`, {
            responseType: "blob",
          }),
      },
      {
        id: "push-document-package",
        method: "POST",
        label: "Push/stage share package",
        path: "/files/push_document_package/{dealId}",
        needsLoan: true,
        body: JSON.stringify({ selector: "third_party_review" }, null, 2),
        bodyHelp: "{ selector, target? } — selector as above; returns the staged package URL.",
        run: (http, c) =>
          http.post(`/files/push_document_package/${c.loanId}`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Checklists — global task repository",
    blurb:
      "The GLOBAL task-template repository (not the per-loan tasklist the FE already uses). Reads are safe; create/update/delete mutate shared templates — use with care. /checklists/task_repository/* + /checklists/plugin.",
    endpoints: [
      {
        id: "checklist-plugin",
        method: "GET",
        label: "Checklist plugin metadata",
        path: "/checklists/plugin",
        needsLoan: false,
        run: (http) => http.get(`/checklists/plugin`),
      },
      {
        id: "task-repository-all",
        method: "GET",
        label: "Task repository (all templates)",
        path: "/checklists/task_repository",
        needsLoan: false,
        run: (http) => http.get(`/checklists/task_repository`),
      },
      {
        id: "task-repository-create",
        method: "POST",
        label: "Create a repository task template",
        path: "/checklists/task_repository/task",
        needsLoan: false,
        body: JSON.stringify({ name: "Test task (tester)" }, null, 2),
        bodyHelp: "The task-template payload. MUTATES the shared repository.",
        run: (http, c) => http.post(`/checklists/task_repository/task`, c.body),
      },
      {
        id: "task-repository-update",
        method: "POST",
        label: "Update a repository task template",
        path: "/checklists/task_repository/task/{taskId}/update",
        needsLoan: false,
        fields: [{ key: "taskId", label: "taskId", width: 260 }],
        body: JSON.stringify({}, null, 2),
        bodyHelp: "Partial $set. MUTATES the shared repository.",
        run: (http, c) =>
          http.post(`/checklists/task_repository/task/${c.f.taskId}/update`, c.body),
      },
      {
        id: "task-repository-delete",
        method: "DELETE",
        label: "Delete a repository task template",
        path: "/checklists/task_repository/task/{taskId}",
        needsLoan: false,
        fields: [{ key: "taskId", label: "taskId", width: 260 }],
        run: (http, c) =>
          http.delete(`/checklists/task_repository/task/${c.f.taskId}`),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Model schemas & reference options",
    blurb:
      "The two /model_schemas reads the FE doesn't use (object/{type} IS used for fetched selections). Base-model dataframe + assignment options. Read-only.",
    endpoints: [
      {
        id: "base-models-df",
        method: "GET",
        label: "Base models dataframe",
        path: "/model_schemas/base_models_df",
        needsLoan: false,
        run: (http) => http.get(`/model_schemas/base_models_df`),
      },
      {
        id: "assignment-options",
        method: "GET",
        label: "Assignment options",
        path: "/model_schemas/assignment_options",
        needsLoan: false,
        run: (http) => http.get(`/model_schemas/assignment_options`),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Realtime — correspondent presence",
    blurb:
      "Correspondent-scoped presence over Web PubSub (the FE wires loan presence but not this). Needs a connectionId from /realtime/negotiate. 503 = realtime not provisioned. /realtime/correspondent/*.",
    endpoints: [
      {
        id: "corr-presence-enter",
        method: "POST",
        label: "Correspondent presence — enter",
        path: "/realtime/correspondent/enter",
        needsLoan: false,
        usesConnection: true,
        fields: [{ key: "companyId", label: "companyId", width: 220 }],
        bodyHelp: "Sends { connectionId, companyId }.",
        run: (http, c) =>
          http.post(`/realtime/correspondent/enter`, {
            connectionId: c.connectionId,
            companyId: c.f.companyId,
          }),
      },
      {
        id: "corr-presence-exit",
        method: "POST",
        label: "Correspondent presence — exit",
        path: "/realtime/correspondent/exit",
        needsLoan: false,
        usesConnection: true,
        fields: [{ key: "companyId", label: "companyId", width: 220 }],
        bodyHelp: "Sends { connectionId, companyId }.",
        run: (http, c) =>
          http.post(`/realtime/correspondent/exit`, {
            connectionId: c.connectionId,
            companyId: c.f.companyId,
          }),
      },
      {
        id: "corr-test-notify",
        method: "POST",
        label: "Correspondent test broadcast",
        path: "/realtime/correspondent/{companyId}/test_notify",
        needsLoan: false,
        fields: [{ key: "companyId", label: "companyId", width: 220 }],
        body: JSON.stringify({ message: "test" }, null, 2),
        bodyHelp: "Broadcasts a test notification to the correspondent's room.",
        run: (http, c) =>
          http.post(`/realtime/correspondent/${c.f.companyId}/test_notify`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Underwriting — misc unused",
    blurb:
      "Loan-level actions present on the underwriting router but never called by the FE.",
    endpoints: [
      {
        id: "inbound-email-comment",
        method: "POST",
        label: "Add inbound-email comment",
        path: "/underwriting/inbound_email_comment/{dealId}",
        needsLoan: true,
        body: JSON.stringify({ htmlString: "<p>inbound email body</p>" }, null, 2),
        bodyHelp: "Comment payload flagged as inbound-email sourced.",
        run: (http, c) =>
          http.post(`/underwriting/inbound_email_comment/${c.loanId}`, c.body),
      },
      {
        id: "pull-credit-report",
        method: "POST",
        label: "Pull credit report for a borrower",
        path: "/underwriting/pull_credit_report_borrower/{deal_id}/{app_id}",
        needsLoan: true,
        fields: [{ key: "appId", label: "app_id", placeholder: "credit-auth application id", width: 260 }],
        bodyHelp: "Triggers a credit pull — may incur a real vendor call. Use a test loan.",
        run: (http, c) =>
          http.post(
            `/underwriting/pull_credit_report_borrower/${c.loanId}/${c.f.appId}`,
            {},
          ),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Correspondents — MLPA docs, typed contacts & editor",
    blurb:
      "Correspondent-admin capabilities the FE doesn't surface (correspondents is a build-dependency, not routed). Typed contact CRUD, MLPA document version control (versions/diff/status/index-terms), and a config editor blob. All keyed by companyId (get one from GET /correspondents/list). Document upload is multipart so it isn't testable here. /correspondents/{companyId}/*.",
    endpoints: [
      {
        id: "corr-contact-add",
        method: "POST",
        label: "Add typed contact",
        path: "/correspondents/{companyId}/contacts/{contactType}",
        needsLoan: false,
        fields: [
          { key: "companyId", label: "companyId", placeholder: "correspondent id", width: 220 },
          {
            key: "contactType",
            label: "contactType",
            default: "additionalContacts",
            placeholder: "additionalContacts | authorizedSigners | servicingContacts",
            width: 260,
          },
        ],
        body: JSON.stringify({ firstName: "Test", lastName: "Contact", emailAddress: "test@example.com" }, null, 2),
        bodyHelp: "A CorrespondentContact payload.",
        run: (http, c) =>
          http.post(
            `/correspondents/${c.f.companyId}/contacts/${c.f.contactType}`,
            c.body,
          ),
      },
      {
        id: "corr-contact-update",
        method: "POST",
        label: "Update typed contact",
        path: "/correspondents/{companyId}/contacts/{contactType}/{contactId}/update",
        needsLoan: false,
        fields: [
          { key: "companyId", label: "companyId", width: 220 },
          { key: "contactType", label: "contactType", default: "additionalContacts", width: 260 },
          { key: "contactId", label: "contactId", placeholder: "contact _id", width: 220 },
        ],
        body: JSON.stringify({ phoneNumber: "555-0100" }, null, 2),
        bodyHelp: "Partial $set of contact fields.",
        run: (http, c) =>
          http.post(
            `/correspondents/${c.f.companyId}/contacts/${c.f.contactType}/${c.f.contactId}/update`,
            c.body,
          ),
      },
      {
        id: "corr-contact-delete",
        method: "DELETE",
        label: "Delete typed contact",
        path: "/correspondents/{companyId}/contacts/{contactType}/{contactId}",
        needsLoan: false,
        fields: [
          { key: "companyId", label: "companyId", width: 220 },
          { key: "contactType", label: "contactType", default: "additionalContacts", width: 260 },
          { key: "contactId", label: "contactId", width: 220 },
        ],
        run: (http, c) =>
          http.delete(
            `/correspondents/${c.f.companyId}/contacts/${c.f.contactType}/${c.f.contactId}`,
          ),
      },
      {
        id: "corr-documents-list",
        method: "GET",
        label: "List correspondent documents",
        path: "/correspondents/{companyId}/documents",
        needsLoan: false,
        fields: [{ key: "companyId", label: "companyId", width: 220 }],
        run: (http, c) => http.get(`/correspondents/${c.f.companyId}/documents`),
      },
      {
        id: "corr-document-download",
        method: "GET",
        label: "Download a correspondent document",
        path: "/correspondents/{companyId}/documents/{docId}/download",
        needsLoan: false,
        fields: [
          { key: "companyId", label: "companyId", width: 220 },
          { key: "docId", label: "docId", placeholder: "document _id", width: 220 },
          { key: "inline", label: "inline? (true/false)", default: "false", width: 160 },
        ],
        responseType: "blob",
        run: (http, c) =>
          http.get(`/correspondents/${c.f.companyId}/documents/${c.f.docId}/download`, {
            params: params({ inline: c.f.inline === "true" ? "true" : "" }),
            responseType: "blob",
          }),
      },
      {
        id: "corr-document-status",
        method: "POST",
        label: "Update document status (→ current MLPA)",
        path: "/correspondents/{companyId}/documents/{docId}/status",
        needsLoan: false,
        fields: [
          { key: "companyId", label: "companyId", width: 220 },
          { key: "docId", label: "docId", width: 220 },
        ],
        body: JSON.stringify({ status: "current" }, null, 2),
        bodyHelp: "Document status update; promoting an MLPA to current sets it as the active version.",
        run: (http, c) =>
          http.post(
            `/correspondents/${c.f.companyId}/documents/${c.f.docId}/status`,
            c.body,
          ),
      },
      {
        id: "corr-mlpa-versions",
        method: "GET",
        label: "MLPA versions",
        path: "/correspondents/{companyId}/mlpa/versions",
        needsLoan: false,
        fields: [{ key: "companyId", label: "companyId", width: 220 }],
        run: (http, c) => http.get(`/correspondents/${c.f.companyId}/mlpa/versions`),
      },
      {
        id: "corr-mlpa-diff",
        method: "GET",
        label: "MLPA diff (two versions)",
        path: "/correspondents/{companyId}/mlpa/diff",
        needsLoan: false,
        fields: [
          { key: "companyId", label: "companyId", width: 220 },
          { key: "fromDocId", label: "fromDocId (optional)", placeholder: "older doc _id", width: 220 },
          { key: "toDocId", label: "toDocId (optional)", placeholder: "defaults to latest", width: 220 },
        ],
        run: (http, c) =>
          http.get(`/correspondents/${c.f.companyId}/mlpa/diff`, {
            params: params({ fromDocId: c.f.fromDocId, toDocId: c.f.toDocId }),
          }),
      },
      {
        id: "corr-index-terms",
        method: "POST",
        label: "Index MLPA terms from a document",
        path: "/correspondents/{companyId}/documents/{docId}/index_terms",
        needsLoan: false,
        fields: [
          { key: "companyId", label: "companyId", width: 220 },
          { key: "docId", label: "docId", width: 220 },
        ],
        run: (http, c) =>
          http.post(
            `/correspondents/${c.f.companyId}/documents/${c.f.docId}/index_terms`,
            {},
          ),
      },
      {
        id: "corr-editor-get",
        method: "GET",
        label: "Get correspondent editor blob",
        path: "/correspondents/{companyId}/editor",
        needsLoan: false,
        fields: [{ key: "companyId", label: "companyId", width: 220 }],
        run: (http, c) => http.get(`/correspondents/${c.f.companyId}/editor`),
      },
      {
        id: "corr-editor-save",
        method: "POST",
        label: "Save correspondent editor blob",
        path: "/correspondents/{companyId}/editor",
        needsLoan: false,
        fields: [{ key: "companyId", label: "companyId", width: 220 }],
        body: JSON.stringify({}, null, 2),
        bodyHelp: "The editor config payload to persist.",
        run: (http, c) =>
          http.post(`/correspondents/${c.f.companyId}/editor`, c.body),
      },
      {
        id: "corr-overlays-list",
        method: "GET",
        label: "List correspondent overlays",
        path: "/correspondents/{companyId}/overlays",
        needsLoan: false,
        fields: [{ key: "companyId", label: "companyId", width: 220 }],
        bodyHelp:
          "The LIST read. Note the FE wires the SINGULAR /overlay create/update/delete routes but never this one.",
        run: (http, c) => http.get(`/correspondents/${c.f.companyId}/overlays`),
      },
      {
        id: "corr-attribute-by-email",
        method: "GET",
        label: "Attribute a correspondent by email",
        path: "/correspondents/attribute/by_email/{email}",
        needsLoan: false,
        fields: [
          { key: "email", label: "email", placeholder: "person@company.com", width: 280 },
        ],
        bodyHelp: "Resolves which correspondent an email address belongs to.",
        run: (http, c) =>
          http.get(
            `/correspondents/attribute/by_email/${encodeURIComponent(c.f.email)}`,
          ),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Funding statements — LFS / TFS / LPS lifecycle",
    blurb:
      "An entire router the FE has never called. One generated fee/economics statement per funding channel: computed PROJECTION → closer LOCK (snapshot + PDF filed to loanDocuments) → counterparty ACKNOWLEDGMENT. Includes the Net Wire Calculator (funding-to-title balancing). statementKind is chosen by the loan's fundingType, not by you. Documented in funding-statements.md.",
    endpoints: [
      {
        id: "fs-get",
        method: "GET",
        label: "Get current statement (projection or locked snapshot)",
        path: "/funding_statements/{dealId}",
        needsLoan: true,
        bodyHelp:
          "statementKind = LFS | TFS (auto by fundingType); statementStatus = Projection | Locked | Acknowledged.",
        run: (http, c) => http.get(`/funding_statements/${c.loanId}`),
      },
      {
        id: "fs-pdf",
        method: "GET",
        label: "Statement PDF (inline)",
        path: "/funding_statements/{dealId}/pdf",
        needsLoan: true,
        responseType: "blob",
        bodyHelp:
          "Renders the projection on the fly pre-lock; same layout post-lock. Returns application/pdf.",
        run: (http, c) =>
          http.get(`/funding_statements/${c.loanId}/pdf`, {
            responseType: "blob",
          }),
      },
      {
        id: "fs-lock",
        method: "POST",
        label: "Lock statement (CLOSER ACTION — writes a document)",
        path: "/funding_statements/{dealId}/lock",
        needsLoan: true,
        body: JSON.stringify({}, null, 2),
        bodyHelp:
          "⚠️ Not read-only. Snapshots, renders, and FILES a PDF into the loan's documents (docGroup 'Closing Package'), then publishes funding_statement_locked. Idempotent, but it does mutate the loan.",
        run: (http, c) =>
          http.post(`/funding_statements/${c.loanId}/lock`, c.body),
      },
      {
        id: "fs-acknowledge",
        method: "POST",
        label: "Acknowledge statement",
        path: "/funding_statements/{dealId}/acknowledge",
        needsLoan: true,
        body: JSON.stringify({ name: "", method: "signed_pdf" }, null, 2),
        bodyHelp:
          "{ name, method } — method is 'signed_pdf' today ('portal_esign' is future). 409 when nothing is locked.",
        run: (http, c) =>
          http.post(`/funding_statements/${c.loanId}/acknowledge`, c.body),
      },
      {
        id: "fs-net-wire",
        method: "GET",
        label: "Net Wire Calculator",
        path: "/funding_statements/{dealId}/net_wire",
        needsLoan: true,
        bodyHelp:
          "Wire build-down (advance + credits − fees − escrows − reserve → netWireToTitle) PLUS the HUD sources/uses balance with hudBalanceVariance (zero ±$1 = balanced). hudCompareSource says whether the title side came from a HUD extract or was 'prepared' from loan fields.",
        run: (http, c) => http.get(`/funding_statements/${c.loanId}/net_wire`),
      },
      {
        id: "fs-net-wire-pdf",
        method: "GET",
        label: "Net Wire Calculator PDF (inline)",
        path: "/funding_statements/{dealId}/net_wire/pdf",
        needsLoan: true,
        responseType: "blob",
        run: (http, c) =>
          http.get(`/funding_statements/${c.loanId}/net_wire/pdf`, {
            responseType: "blob",
          }),
      },
      {
        id: "fs-trade-get",
        method: "GET",
        label: "Get bulk-trade LPS",
        path: "/funding_statements/trade/{tradeId}",
        needsLoan: false,
        fields: [{ key: "tradeId", label: "tradeId", width: 240 }],
        bodyHelp:
          "Per-loan pricing-attribution rows + computed header rollups. Keyed by tradeId, NOT dealId.",
        run: (http, c) => http.get(`/funding_statements/trade/${c.f.tradeId}`),
      },
      {
        id: "fs-trade-lock",
        method: "POST",
        label: "Lock trade LPS (writes a document to EVERY loan)",
        path: "/funding_statements/trade/{tradeId}/lock",
        needsLoan: false,
        fields: [
          {
            key: "tradeId",
            label: "tradeId",
            placeholder: "a tradeId, or 'adhoc'",
            width: 240,
          },
        ],
        body: JSON.stringify({ dealIds: [] }, null, 2),
        bodyHelp:
          "⚠️ Not read-only — saves the acknowledgment PDF to EVERY loan in the trade (documentOidsByDeal). For an ad-hoc group use tradeId 'adhoc' and pass dealIds; otherwise send {}.",
        run: (http, c) =>
          http.post(`/funding_statements/trade/${c.f.tradeId}/lock`, c.body),
      },
      {
        id: "fs-trade-acknowledge",
        method: "POST",
        label: "Acknowledge trade LPS (seller)",
        path: "/funding_statements/trade/{tradeId}/acknowledge",
        needsLoan: false,
        fields: [{ key: "tradeId", label: "tradeId", width: 240 }],
        body: JSON.stringify({ name: "", method: "signed_pdf" }, null, 2),
        run: (http, c) =>
          http.post(
            `/funding_statements/trade/${c.f.tradeId}/acknowledge`,
            c.body,
          ),
      },
      // POST /funding_statements/{dealId}/pdf and .../trade/{tradeId}/pdf are
      // MULTIPART (attach a frontend-rendered PDF to a locked snapshot) — left
      // out per this harness's no-file-upload policy.
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Ocrolus — book lifecycle (/files)",
    blurb:
      "Bank-statement analysis. One BOOK per loan, anchored on Ocrolus xid = dealId. Sending statements is already wired on the Documents tab; these four drive the book itself. NOTE the real prefix is /files — the backend FE doc writes /loan_files, which is wrong. Documented in ocrolus-book-and-notes.md.",
    endpoints: [
      {
        id: "ocrolus-create-book",
        method: "POST",
        label: "Create book from loan (idempotent)",
        path: "/files/create_ocrolus_book/{dealId}",
        needsLoan: true,
        bodyHelp:
          "No body. Returns the EXISTING book if one exists rather than creating a second. → { dealId, book_uuid, book_name, filesSent, message }",
        run: (http, c) => http.post(`/files/create_ocrolus_book/${c.loanId}`, {}),
      },
      {
        id: "ocrolus-book-status",
        method: "GET",
        label: "Book status",
        path: "/files/ocrolus_book/{dealId}",
        needsLoan: true,
        bodyHelp:
          "book_uuid null = no book. filesSent > 0 with accountsReady false = normal 'processing' (results arrive by webhook). signals.riskLevel HIGH/MEDIUM already created a review condition + notification server-side.",
        run: (http, c) => http.get(`/files/ocrolus_book/${c.loanId}`),
      },
      {
        id: "ocrolus-books-list",
        method: "GET",
        label: "Browse books on the shared account",
        path: "/files/ocrolus_books",
        needsLoan: false,
        fields: [
          {
            key: "scope",
            label: "scope",
            default: "all",
            placeholder: "all | portal",
            width: 180,
          },
        ],
        bodyHelp:
          "scope=all (default) is UNFILTERED — both portals see the whole account; every row carries a portalHint ('lq'|'ev'|'unknown'). scope=portal applies the leakage guard and reports hiddenCount.",
        run: (http, c) =>
          http.get(`/files/ocrolus_books`, { params: params({ scope: c.f.scope }) }),
      },
      {
        id: "ocrolus-assign-book",
        method: "POST",
        label: "Assign an existing book to this loan",
        path: "/files/assign_ocrolus_book/{dealId}",
        needsLoan: true,
        body: JSON.stringify({ book_uuid: "", rename: true, force: false }, null, 2),
        bodyHelp:
          "Transition path for books created outside the LOS. RENAMES the book to the loan's EV#/LQ# and re-anchors xid = dealId, then pulls results + fraud signals inline.",
        run: (http, c) =>
          http.post(`/files/assign_ocrolus_book/${c.loanId}`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Loan lifecycle — archive / restore / pipeline transfer",
    blurb:
      "⚠️ STATE TRANSITIONS, not reads. Archive soft-removes a loan into archived_loans (restorable). The two move_* routes shift a loan between the pre-close underwriting pipeline and post-close acquisitions — the deal _id is PRESERVED, so documents, blob container, extracts and the Box binding stay attached, but pipeline-specific fields are DROPPED and loanStatus is remapped. Transfer is documented in pipeline-transfer.md; archive/restore is undocumented.",
    endpoints: [
      {
        id: "archive-loan",
        method: "POST",
        label: "Archive loan",
        path: "/underwriting/archive_loan/{dealId}",
        needsLoan: true,
        body: JSON.stringify({ reason: "" }, null, 2),
        bodyHelp: "Soft-remove into archived_loans. Restorable via restore_loan.",
        run: (http, c) =>
          http.post(`/underwriting/archive_loan/${c.loanId}`, c.body),
      },
      {
        id: "archived-loans",
        method: "GET",
        label: "List archived loans",
        path: "/underwriting/archived_loans",
        needsLoan: false,
        run: (http) => http.get(`/underwriting/archived_loans`),
      },
      {
        id: "restore-loan",
        method: "POST",
        label: "Restore archived loan",
        path: "/underwriting/restore_loan/{dealId}",
        needsLoan: true,
        bodyHelp: "Moves an archived loan back into the active pipeline.",
        run: (http, c) => http.post(`/underwriting/restore_loan/${c.loanId}`, {}),
      },
      {
        id: "move-to-acquisitions",
        method: "POST",
        label: "Move pre-close loan → acquisitions",
        path: "/underwriting/move_to_acquisitions/{dealId}",
        needsLoan: true,
        body: JSON.stringify({ statusOverride: "", keepSource: false }, null, 2),
        bodyHelp:
          "⚠️ DROPS pre-close-only fields (closingCutoffDate, closingScheduled*, creditAuthorizations, applications, closingStatus, fundingShieldStatus). expectedClosingDate + lock fields are kept. Body is optional — send {} for defaults.",
        run: (http, c) =>
          http.post(`/underwriting/move_to_acquisitions/${c.loanId}`, c.body),
      },
      {
        id: "move-to-underwriting",
        method: "POST",
        label: "Move acquisition → pre-close underwriting",
        path: "/loan_acquisitions/move_to_underwriting/{dealId}",
        needsLoan: true,
        body: JSON.stringify({ statusOverride: "", keepSource: false }, null, 2),
        bodyHelp:
          "⚠️ The mirror of the above — DROPS acquisition-only trade / closed-package fields. Body optional.",
        run: (http, c) =>
          http.post(`/loan_acquisitions/move_to_underwriting/${c.loanId}`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "AI reviews — merge findings & Sourcepoint fetch (agent_reviews.py)",
    blurb:
      "The two agent_reviews routes the drilldown's AI Review tab does NOT call (everything else on that router is wired via losAiReviewsApi). Merge is documented in ai-activity-audit-and-condition-merge.md; sourcepoint-fetch in note-terms-verification.md.",
    endpoints: [
      {
        id: "ai-findings-merge",
        method: "POST",
        label: "Merge duplicate staged findings",
        path: "/admin/loans/{dealId}/ai-reviews/findings/merge",
        needsLoan: true,
        body: JSON.stringify(
          {
            candidateIds: [],
            reason: "",
            mergedTitle: "",
            mergedBody: "",
            requirementType: "Condition",
          },
          null,
          2,
        ),
        bodyHelp:
          "Admin only. candidateIds needs ≥2, all staged and all on this loan. `reason` is REQUIRED (it is the eval label). Sources become status 'merged' and leave the queue; the merged row is a normal staged finding. requirementType: 'Document' | 'Condition'.",
        run: (http, c) =>
          http.post(`/admin/loans/${c.loanId}/ai-reviews/findings/merge`, c.body),
      },
      {
        id: "sourcepoint-fetch",
        method: "POST",
        label: "Fetch Sourcepoint closing package",
        path: "/admin/loans/{dealId}/sourcepoint-fetch",
        needsLoan: true,
        bodyHelp:
          "Admin or underwriter. NOT an AI review — a document ingest with no run-state. Publishes to the sourcepoint_fetch topic; the listener does SFTP download → 'Source Point - <Type>' docs (sha1-idempotent) → extraction sweep → stamps closingPackageReceivedDate. Safe to re-trigger. Watch the Documents tab for completion, not this response.",
        run: (http, c) =>
          http.post(`/admin/loans/${c.loanId}/sourcepoint-fetch`, {}),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Acquisitions — B-piece validation",
    blurb:
      "Validates the correspondent-retained junior piece of the note (the trade conveys/funds the SENIOR piece only). Read-mostly: persists only bPiece / bPieceValidated / bPieceValidationNotes; staged CandidateFinding rows are RETURNED for operator promotion, never auto-persisted. Undocumented — found by the route diff.",
    endpoints: [
      {
        id: "validate-b-piece",
        method: "POST",
        label: "Validate B-piece",
        path: "/loan_acquisitions/validate_b_piece/{dealId}",
        needsLoan: true,
        body: JSON.stringify({ documents: [] }, null, 2),
        bodyHelp:
          "Optional { documents: [{name, docType, sourceDocumentId}] } adds caller-supplied docs to the scan (mirrors validate_mortgagee, which IS wired). Send {} to scan only what's on the loan. Checks: agreement doc present; bPieceAmount corroborated by extracts and/or an exact trade-economics delta; the silent case of an agreement on file while bPiece=False.",
        run: (http, c) =>
          http.post(`/loan_acquisitions/validate_b_piece/${c.loanId}`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Prospect intake & generated application links",
    blurb:
      "loportal routes the FE either never calls or calls under a DEAD prefix. ⚠️ The two deal_generated_link calls exist in useBorrowerSubmissionsHook under ROUTE_PREFIX 'borrower_submissions' — a router that is commented out in main.py, so they 404 today. The real prefix is /loportal.",
    endpoints: [
      {
        id: "deal-link-application",
        method: "POST",
        label: "Generate loan-application link",
        path: "/loportal/deal_generated_link/loan_application/{dealId}",
        needsLoan: true,
        body: JSON.stringify({}, null, 2),
        bodyHelp:
          "Mints a unique application id tied to the logged-in user + this deal (the 'copy application link' action).",
        run: (http, c) =>
          http.post(
            `/loportal/deal_generated_link/loan_application/${c.loanId}`,
            c.body,
          ),
      },
      {
        id: "deal-link-credit-auth",
        method: "POST",
        label: "Generate credit-authorization link",
        path: "/loportal/deal_generated_link/credit_authorization/{dealId}",
        needsLoan: true,
        body: JSON.stringify({}, null, 2),
        run: (http, c) =>
          http.post(
            `/loportal/deal_generated_link/credit_authorization/${c.loanId}`,
            c.body,
          ),
      },
      {
        id: "create-deal-from-email",
        method: "POST",
        label: "Create deal from email",
        path: "/loportal/create_deal_from_email",
        needsLoan: false,
        body: JSON.stringify({}, null, 2),
        bodyHelp:
          "⚠️ CREATES A LOAN. Extracts subject + attachments, creates the loan named from the subject, uploads attachments, adds a comment with the body, generates app + credit-auth links, and EMAILS the sender. Server-side intake path — there is no obvious FE trigger.",
        run: (http, c) => http.post(`/loportal/create_deal_from_email`, c.body),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Borrower portal admin (staging + invites)",
    blurb:
      "LOS-side management of borrower-portal users. Only relevant if we decide staff should administer portal accounts from this app. Undocumented — found by the route diff.",
    endpoints: [
      {
        id: "bp-create-profile-invite",
        method: "POST",
        label: "Create borrower profile + invite",
        path: "/borrower_portal_admin/create_borrower_profile_and_invite",
        needsLoan: false,
        body: JSON.stringify({ sendInvite: true }, null, 2),
        bodyHelp:
          "⚠️ Sends a real invite email unless sendInvite is false. Creates the profile, stages the record, and invites in one request. Legacy alias create_borrower_profile_staging hits the same handler.",
        run: (http, c) =>
          http.post(
            `/borrower_portal_admin/create_borrower_profile_and_invite`,
            c.body,
          ),
      },
      {
        id: "bp-send-invite-from-staging",
        method: "POST",
        label: "Re-send invite from staged record",
        path: "/borrower_portal_admin/send_borrower_profile_invite_from_staging",
        needsLoan: false,
        body: JSON.stringify({}, null, 2),
        bodyHelp:
          "⚠️ Sends a real email. Manual recovery / backfill for an already-staged borrower.",
        run: (http, c) =>
          http.post(
            `/borrower_portal_admin/send_borrower_profile_invite_from_staging`,
            c.body,
          ),
      },
      {
        id: "bp-create-app-existing",
        method: "POST",
        label: "Push application onto a live portal user",
        path: "/borrower_portal_admin/create_application_for_existing_borrower/{borrowerId}",
        needsLoan: false,
        fields: [{ key: "borrowerId", label: "borrower_id", width: 240 }],
        body: JSON.stringify(
          { applicationData: {}, sendNotification: true },
          null,
          2,
        ),
        bodyHelp:
          "For a borrower who ALREADY has an account — no staging or invite needed. Lands on their stagedApplications.",
        run: (http, c) =>
          http.post(
            `/borrower_portal_admin/create_application_for_existing_borrower/${c.f.borrowerId}`,
            c.body,
          ),
      },
      {
        id: "bp-reconcile-ids",
        method: "POST",
        label: "Reconcile staged ↔ live user ids",
        path: "/borrower_portal_admin/reconcile_staged_live_user_ids",
        needsLoan: false,
        bodyHelp:
          "⚠️ Ops repair tool — rewrites staged records to share the live record _id and fixes Stytch metadata. Run deliberately.",
        run: (http) =>
          http.post(`/borrower_portal_admin/reconcile_staged_live_user_ids`, {}),
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  {
    group: "Misc unwired reads & writes",
    blurb:
      "The stragglers from the route audit — each one real, served, and unused. See ENDPOINT_INTEGRATION_BACKLOG.md for where each belongs.",
    endpoints: [
      {
        id: "loan-documents-lazy",
        method: "GET",
        label: "Loan documents (lazy metadata read)",
        path: "/files/loan_documents/{dealId}",
        needsLoan: true,
        bodyHelp:
          "One indexed find against loan_documents — no blob-container listing. Lets the Documents tab refresh after uploads without refetching the whole deal. → { loanDocuments: [...] }",
        run: (http, c) => http.get(`/files/loan_documents/${c.loanId}`),
      },
      {
        id: "liquidity-source-delete",
        method: "DELETE",
        label: "Delete a liquidity source",
        path: "/liquidity/sources/{dealId}/{sourceId}",
        needsLoan: true,
        fields: [{ key: "sourceId", label: "sourceId", width: 240 }],
        bodyHelp:
          "⚠️ THE FIX FOR A LIVE BUG: useLiquidityHook posts to /liquidity/sources/{dealId}/{sourceId}/remove, which does not exist — deleting a liquidity source 404s in the app today. THIS is the real route. Returns the full updated list.",
        run: (http, c) =>
          http.delete(`/liquidity/sources/${c.loanId}/${c.f.sourceId}`),
      },
      {
        id: "appraisal-order-detail",
        method: "GET",
        label: "Appraisal order detail",
        path: "/loan-processing/appraisal-orders/{orderId}",
        needsLoan: false,
        fields: [{ key: "orderId", label: "orderId", width: 240 }],
        bodyHelp:
          "The single-order read. The /queue list and the /{orderId}/{action} transitions ARE wired; only this detail GET is unused.",
        run: (http, c) =>
          http.get(`/loan-processing/appraisal-orders/${c.f.orderId}`),
      },
      {
        id: "groups-members-bulk",
        method: "POST",
        label: "Add group members in bulk",
        path: "/groups/{groupId}/members/bulk",
        needsLoan: false,
        fields: [{ key: "groupId", label: "group_id", width: 240 }],
        body: JSON.stringify({ emails: [] }, null, 2),
        bodyHelp:
          "{ emails: [...] } OR { members: [{email, fullName}] }. Dedups against the group. The FE only does single add/remove today.",
        run: (http, c) =>
          http.post(`/groups/${c.f.groupId}/members/bulk`, c.body),
      },
      {
        id: "dscr-pricing-agent-review",
        method: "POST",
        label: "DSCR pricing — on-demand agent review",
        path: "/admin/data/dscr-pricing/{cpId}/agent-review",
        needsLoan: false,
        fields: [{ key: "cpId", label: "cpId", width: 220 }],
        body: JSON.stringify({}, null, 2),
        bodyHelp:
          "The multi-counterparty review surface — agent review on a stored version. The rest of /admin/data/dscr-pricing IS wired via adminDataApi.",
        run: (http, c) =>
          http.post(`/admin/data/dscr-pricing/${c.f.cpId}/agent-review`, c.body),
      },
      {
        id: "notifications-file-doc-types",
        method: "GET",
        label: "File doc types (notifications copy)",
        path: "/notifications/file_doc_types",
        needsLoan: false,
        bodyHelp:
          "A DUPLICATE of /underwriting/file_doc_types, which the app already uses. Here for completeness — don't wire it.",
        run: (http) => http.get(`/notifications/file_doc_types`),
      },
    ],
  },
];

export default ENDPOINT_GROUPS;
